import { useEffect, useState } from 'react';
import { Rocket, Loader2, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { storeApi } from '../lib/api';
import { detectControls } from '../lib/analyze';

export function PublishButton({ game, updateGame, compact = false }) {
  const [open, setOpen] = useState(false);
  const published = !!game.storeId;
  const outdated = published && game.publishedHead !== game.headId;
  return (
    <>
      <button data-testid={`publish-open-btn${compact ? '-compact' : ''}`} onClick={() => setOpen(true)} disabled={game.blank} title="Pubblica nello Store"
        className={compact
          ? `h-8 rounded-lg px-2.5 flex items-center gap-1.5 text-[10px] font-bold transition-colors disabled:opacity-30 ${published && !outdated ? 'text-emerald-300 border border-emerald-500/40' : 'bg-gradient-to-r from-violet-600 to-cyan-600 text-white'}`
          : 'w-full flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold bg-gradient-to-r from-violet-600 to-cyan-600 hover:brightness-110 transition-[filter] disabled:opacity-30 shadow-[0_0_24px_-8px_rgb(var(--violet-500))]'}>
        <Rocket className="h-4 w-4" />{compact ? (published ? (outdated ? 'AGGIORNA' : 'NELLO STORE') : 'PUBBLICA') : published ? (outdated ? 'Aggiorna nello Store' : 'Pubblicato nello Store') : 'Pubblica nello Store'}
      </button>
      <PublishDialog open={open} onOpenChange={setOpen} game={game} updateGame={updateGame} />
    </>
  );
}

function PublishDialog({ open, onOpenChange, game, updateGame }) {
  const [title, setTitle] = useState(game.title);
  const [genre, setGenre] = useState(game.genre);
  const [desc, setDesc] = useState(game.storeDescription || '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setTitle(game.title); setGenre(game.genre); } }, [open]); // eslint-disable-line

  const publish = async () => {
    setBusy(true);
    try {
      const r = await storeApi.publish({ source_id: game.id, title: title.trim().slice(0, 60), genre: genre.trim().slice(0, 30) || 'Arcade', description: desc.trim(), code: game.code, controls: detectControls(game.code), cover: game.cover });
      updateGame((g) => ({ ...g, storeId: r.id, publishedAt: Date.now(), publishedHead: g.headId, storeDescription: desc.trim() }));
      toast.success(r.updated ? 'Gioco aggiornato nello Store' : 'Pubblicato nella vetrina pubblica!');
      onOpenChange(false);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const unpublish = async () => {
    setBusy(true);
    try {
      await storeApi.unpublish(game.storeId);
      updateGame((g) => ({ ...g, storeId: null, publishedHead: null }));
      toast.success('Rimosso dallo Store');
      onOpenChange(false);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="publish-dialog" className="bg-titan-surface border-violet-500/30 max-w-lg">
        <DialogHeader>
          <DialogTitle>{game.storeId ? 'Aggiorna nello Store' : 'Pubblica nello Store'}</DialogTitle>
          <DialogDescription className="t-chat">La versione attuale (HEAD) con copertina diventa giocabile da tutti nella vetrina pubblica, con Mi piace e classifica globale.</DialogDescription>
        </DialogHeader>
        {game.cover && <img src={game.cover} alt="" className="rounded-xl aspect-video object-cover border border-white/10" />}
        <div className="grid grid-cols-[1fr_140px] gap-3">
          <Input data-testid="publish-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titolo" maxLength={60} />
          <Input data-testid="publish-genre" value={genre} onChange={(e) => setGenre(e.target.value)} placeholder="Categoria" maxLength={30} />
        </div>
        <Textarea data-testid="publish-description" value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={500} className="h-24 t-chat text-sm" placeholder="Descrizione per i giocatori (facoltativa)" />
        <button data-testid="publish-submit-btn" onClick={publish} disabled={busy || !title.trim()}
          className="flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-bold bg-gradient-to-r from-violet-600 to-cyan-600 hover:brightness-110 transition-[filter] disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}{game.storeId ? 'Aggiorna versione pubblica' : 'Pubblica ora'}
        </button>
        {game.storeId && (
          <div className="flex gap-2">
            <a data-testid="publish-view-link" href={`/gioco/${game.storeId}`} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-cyan-500/40 py-2 text-xs text-cyan-200 hover:bg-cyan-500/10"><ExternalLink className="h-3.5 w-3.5" />Vedi nello Store</a>
            <button data-testid="unpublish-btn" onClick={unpublish} disabled={busy} className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-rose-500/40 py-2 text-xs text-rose-300 hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5" />Rimuovi dallo Store</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
